// Force Rebuild 4
import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './i18n';
import './index.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ScrollToTop } from './core/components/ScrollToTop';
import { Main } from './main';
import { SearchPage } from './features/search/pages/SearchPage';
import { AreaDetailsPage } from './features/projects/pages/AreaDetailsPage';
import { ProjectDetailsPage } from './features/projects/pages/ProjectDetailsPage';
import { UnitDetailsPage } from './features/projects/pages/UnitDetailsPage';
import { ExplorePage } from './features/search/pages/ExplorePage';
import { NotFoundPage } from './features/public/pages/NotFoundPage';
import { AboutPage } from './features/public/pages/AboutPage';
import { CareersPage } from './features/public/pages/CareersPage';
import { ContactPage } from './features/public/pages/ContactPage';
import { LanguageProvider } from './core/providers/LanguageProvider';
import PublicProjectsPage from './features/projects/pages/ProjectsPage';

// Admin imports
import DashboardLayout from './features/admin/layouts/DashboardLayout';
import LoginPage from './features/admin/auth/LoginPage';
import DashboardPage from './features/admin/dashboard/DashboardPage';
import ProjectsPage from './features/admin/projects/ProjectsPage';
import AdminProjectMapPage from './features/admin/projects/AdminProjectMapPage';
import ProjectFloorsPage from './features/admin/projects/ProjectFloorsPage';
import FloorPlanEditorPage from './features/admin/projects/FloorPlanEditorPage';
import UnitsPage from './features/admin/units/UnitsPage';
import { CategoriesPage } from './features/admin/pages/CategoriesPage';
import PropertyTypesPage from './features/admin/property-types/PropertyTypesPage';
import AreasPage from './features/admin/areas/AreasPage';
import AmenitiesPage from './features/admin/amenities/AmenitiesPage';
import InquiriesPage from './features/admin/inquiries/InquiriesPage';
import UsersPage from './features/admin/users/UsersPage';
import RolesPage from './features/admin/roles/RolesPage';
import BlogPage from './features/admin/blog/BlogPage';
import BlogFormPage from './features/admin/blog/BlogFormPage';
import { BlogListPage } from './features/blog/pages/BlogListPage';
import { BlogPostPage } from './features/blog/pages/BlogPostPage';
import JobsPage from './features/admin/careers/JobsPage';
import InboxPage from './features/admin/contact/InboxPage';
import { NewsletterPage } from './features/admin/newsletter/NewsletterPage';
import { PagesPage } from "./features/admin/pages/PagesPage";
import { PageBuilderPage } from "./features/admin/pages/PageBuilderPage";
import { ContentPage } from "./features/pages/ContentPage";
import BannersPage from './features/admin/cms/BannersPage';
import { SettingsPage } from './features/admin/cms/SettingsPage';
import ProfilePage from './features/admin/profile/ProfilePage';

import { useAuthStore } from './stores/authStore';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            retry: 1,
        },
    },
});

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { token } = useAuthStore();
    if (!token) {
        return <Navigate to="/admin/login" replace />;
    }
    return <>{children}</>;
}

const container = document.querySelector('#root') as Element;
const root = createRoot(container);

root.render(
    <QueryClientProvider client={queryClient}>
        <BrowserRouter>
            <ScrollToTop />
            <LanguageProvider>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<Main />} />
                    <Route path="/explore" element={<ExplorePage />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/projects" element={<PublicProjectsPage />} /> {/* Add Route */}
                    <Route path="/projects/:id" element={<ProjectDetailsPage />} />
                    <Route path="/areas/:id" element={<AreaDetailsPage />} />
                    <Route path="/units/:id" element={<UnitDetailsPage />} />

                    {/* Content Pages */}
                    <Route path="/pages/about-us" element={<AboutPage />} />
                    <Route path="/pages/careers" element={<CareersPage />} />
                    <Route path="/pages/contact-us" element={<ContactPage />} />
                    <Route path="/pages/:slug" element={<ContentPage />} />

                    {/* Blog */}
                    <Route path="/blog" element={<BlogListPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />

                    <Route path="*" element={<NotFoundPage />} />

                    {/* Admin Routes */}
                    <Route path="/admin/login" element={<LoginPage />} />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute>
                                <DashboardLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<DashboardPage />} />
                        <Route path="pages" element={<Navigate to="cms/pages" replace />} />
                        <Route path="projects" element={<ProjectsPage />} />
                        <Route path="projects/:id/map" element={<AdminProjectMapPage type="project" />} />
                        <Route path="projects/:id/floors" element={<ProjectFloorsPage />} />
                        <Route path="projects/:id/floors/:floorId" element={<FloorPlanEditorPage />} />
                        <Route path="categories" element={<CategoriesPage />} />
                        <Route path="property-types" element={<PropertyTypesPage />} />
                        <Route path="units" element={<UnitsPage />} />
                        <Route path="units/:id/map" element={<AdminProjectMapPage type="unit" />} />
                        <Route path="areas" element={<AreasPage />} />
                        <Route path="amenities" element={<AmenitiesPage />} />
                        <Route path="inquiries" element={<InquiriesPage />} />
                        <Route path="users" element={<UsersPage />} />
                        <Route path="roles" element={<RolesPage />} />
                        <Route path="blog" element={<BlogPage />} />
                        <Route path="blog/new" element={<BlogFormPage />} />
                        <Route path="blog/:id/edit" element={<BlogFormPage />} />
                        <Route path="jobs" element={<JobsPage />} />
                        <Route path="jobs" element={<JobsPage />} />
                        <Route path="inbox" element={<InboxPage />} />
                        <Route path="newsletter" element={<NewsletterPage />} />
                        {/* CMS */}
                        <Route path="cms/banners" element={<BannersPage />} />
                        <Route path="cms/pages" element={<PagesPage />} />
                        <Route path="cms/pages/:id/builder" element={<PageBuilderPage />} /> {/* Added Route */}
                        <Route path="cms/settings" element={<SettingsPage />} />
                        <Route path="profile" element={<ProfilePage />} />

                    </Route>
                </Routes>
            </LanguageProvider>
        </BrowserRouter>
    </QueryClientProvider>
);
